import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Printer, Trash2, CheckCircle2 } from 'lucide-react';

import { toast } from 'react-toastify';

import { api } from '../../utils/api';
import { Button, Modal } from '../../components/ui';
import type { Order, OrderItem } from '../../types/@types/order';
import { formatCurrency } from '../../utils/format-currency';
import { usePayOrder, useTakeawayOrders } from '../../hooks/orders';
import { OrderTicketModal } from '../../components/Receipt/OrderTicketModal';
import type { OrderTicketData } from '../../components/Receipt/OrderTicket';
import { TakeawayChargeModal } from '../../components/takeaway/TakeawayChargeModal';
import { useCancelOrder } from '../orders/hooks/useCancelOrder';
import { ConfirmCancelOrderModal } from '../orders/components/ConfirmCancelOrderModal';
import { useOrderStatusMutation } from '../../hooks/useOrders';

const ORDER_STATUS_LABELS: Record<string, string> = {
  WAITING: 'En espera',
  IN_PRODUCTION: 'En producción',
  READY: 'Listo',
  DONE: 'Listo',
  PAID: 'Pagado',
  PARTIAL: 'Pago parcial',
  CANCELLED: 'Cancelado',
  DELIVERED: 'Entregado',
};

interface TakeawayOrderDetailProps {
  order: Order | null;
  open: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onToggleComanda?: (orderId: number, currentPrinted: boolean) => void;
  onPrintComanda?: (order: Order) => void;
}

function formatPrintTime(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

type OrderWithTotals = Order & {
  totals?: {
    subtotal?: number;
    total?: number;
  };
};

type OrderItemWithCalc = OrderItem & {
  linePriceCalc?: { final?: number };
  line_subtotal?: number;
};

function getItemQuantity(order: Order | null): number {
  if (!order?.items) return 0;
  return order.items.reduce(
    (total, item) => total + (Number.isFinite(item.quantity) ? item.quantity : 0),
    0,
  );
}

function getItemDisplayName(item: OrderItem): string {
  const baseName =
    item.name ??
    (typeof item.product === 'string'
      ? item.product
      : item.product?.name ?? 'Producto');
  if (!Number.isFinite(item.quantity) || item.quantity <= 1) {
    return baseName;
  }
  return `${item.quantity} × ${baseName}`;
}

function getItemTotal(item: OrderItem): number {
  const withCalc = item as OrderItemWithCalc;
  const calcFinal = withCalc.linePriceCalc?.final;
  if (typeof calcFinal === 'number' && Number.isFinite(calcFinal)) {
    return calcFinal;
  }

  const line_subtotal = withCalc.line_subtotal;
  if (typeof line_subtotal === 'number' && Number.isFinite(line_subtotal)) {
    return line_subtotal;
  }

  const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
  const base_price =
    typeof withCalc.base_price === 'number' && Number.isFinite(withCalc.base_price)
      ? withCalc.base_price
      : undefined;
  const productPrice =
    typeof item.product !== 'string' &&
      typeof item.product?.price === 'number' &&
      Number.isFinite(item.product.price)
      ? item.product.price
      : 0;

  const unitPrice = base_price ?? productPrice;
  return unitPrice * quantity;
}

function getOrderTotal(order: Order | null): number {
  if (!order) return 0;
  const totals = (order as OrderWithTotals).totals;
  const totalsTotal = totals?.total;
  if (typeof totalsTotal === 'number' && Number.isFinite(totalsTotal)) {
    return totalsTotal;
  }

  if (typeof order.total === 'number' && Number.isFinite(order.total)) {
    return order.total;
  }

  if (!Array.isArray(order.items)) return 0;

  return order.items.reduce((sum, item) => sum + getItemTotal(item), 0);
}

function getCustomerName(order: Order): string {
  return order.customerName?.trim().length
    ? order.customerName.trim()
    : 'Cliente sin nombre';
}

function TakeawayOrderDetail({ order, open, onClose, onCancel, onToggleComanda, onPrintComanda }: TakeawayOrderDetailProps) {
  const total = useMemo(() => getOrderTotal(order), [order]);
  const items = order?.items ?? [];
  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + getItemTotal(item), 0),
    [items],
  );
  const isComandaPrinted = Boolean(order?.comandaPrinted || order?.comanda_printed);
  const printedTime = formatPrintTime(order?.comandaPrintedAt || order?.comanda_printed_at);

  return (
    <Modal
      open={open}
      onClose={onClose}
      titleId="takeaway-order-detail"
      className="max-w-xl"
    >
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold uppercase tracking-wide text-text-muted">
            Pedido para llevar
          </p>
          <h2 className="truncate text-lg font-semibold text-text">
            {order ? getCustomerName(order) : 'Pedido'}
          </h2>
          {order && (order.customerAddress || (order as any).customer_address) && (
            <p className="text-xs text-text font-medium mt-1">
              <span className="text-text-muted">Dirección: </span>{order.customerAddress || (order as any).customer_address}
            </p>
          )}
          {order && (order.customerPhone || (order as any).customer_phone) && (
            <p className="text-xs text-text-muted mt-0.5">
              <span className="text-text-muted">Teléfono: </span>{order.customerPhone || (order as any).customer_phone}
            </p>
          )}
          {order && (
            <div className="mt-2.5 flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  isComandaPrinted
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-800'
                }`}
              >
                {isComandaPrinted ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    <span>Comanda impresa {printedTime ? `(${printedTime})` : ''}</span>
                  </>
                ) : (
                  <>
                    <Printer className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                    <span>Comanda sin imprimir</span>
                  </>
                )}
              </span>
              {onToggleComanda && (
                <button
                  type="button"
                  onClick={() => onToggleComanda(order.id, isComandaPrinted)}
                  className="text-xs text-text-muted hover:text-text underline font-medium cursor-pointer"
                >
                  {isComandaPrinted ? 'Marcar no impresa' : 'Marcar impresa'}
                </button>
              )}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Cerrar detalle"
          onClick={onClose}
          className="h-9 w-9 rounded-full"
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
        {items.length === 0 ? (
          <p className="text-sm text-text-muted">Este pedido no tiene productos.</p>
        ) : (
          <ul className="space-y-4">
            {items.map((item) => {
              const additions = Object.entries(item.additions ?? {});
              const exceptions = item.exceptions ?? [];
              const hasAdditions = additions.length > 0;
              const hasExceptions = exceptions.length > 0;
              return (
                <li key={item.id} className="rounded-xl border border-border px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text">
                        {getItemDisplayName(item)}
                      </p>
                      {item.notes ? (
                        <p className="mt-1 text-xs text-text-muted">Nota: {item.notes}</p>
                      ) : null}
                      {hasAdditions ? (
                        <p className="mt-1 text-xs text-text-muted">
                          Adiciones:{' '}
                          {additions
                            .map(([name, qty]) =>
                              typeof qty === 'number' && qty > 1
                                ? `${qty} × ${name}`
                                : name,
                            )
                            .join(', ')}
                        </p>
                      ) : null}
                      {hasExceptions ? (
                        <p className="mt-1 text-xs text-text-muted">
                          Sin: {exceptions.join(', ')}
                        </p>
                      ) : null}
                    </div>
                    <p className="shrink-0 text-sm font-semibold text-text">
                      {formatCurrency(getItemTotal(item))}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="space-y-2 border-t border-border px-6 py-4">
        {order?.note ? (
          <p className="text-xs text-text-muted">Nota del pedido: {order.note}</p>
        ) : null}
        <div className="flex items-center justify-between text-sm text-text-muted">
          <span>Subtotal</span>
          <span className="font-semibold text-text">{formatCurrency(subtotal)}</span>
        </div>
        <div className="flex items-center justify-between text-base font-semibold text-text mb-4">
          <span>Total</span>
          <span>{formatCurrency(total)}</span>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          {order && onPrintComanda && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPrintComanda(order)}
              className="gap-1.5"
            >
              <Printer className="h-4 w-4" />
              <span>{isComandaPrinted ? 'Reimprimir' : 'Imprimir comanda'}</span>
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          {onCancel && order && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onCancel}
            >
              Cancelar Pedido
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function TakeawayBoard() {
  const navigate = useNavigate();
  const { data: orders, isLoading, isError, error, refetch } = useTakeawayOrders();
  const payOrder = usePayOrder();
  const cancelOrder = useCancelOrder();
  const updateStatus = useOrderStatusMutation();
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);
  const [ticketToPrint, setTicketToPrint] = useState<OrderTicketData | null>(null);
  const [chargingOrder, setChargingOrder] = useState<Order | null>(null);
  const [cancellingOrder, setCancellingOrder] = useState<Order | null>(null);

  const ordersList: Order[] = (Array.isArray(orders)
    ? orders
    : (Array.isArray((orders as any)?.data)
      ? (orders as any).data
      : (orders && typeof orders === 'object' ? Object.values(orders) : []))) as Order[];
  const pendingOrderId = payOrder.variables;
  const errorMessage = error instanceof Error ? error.message : undefined;

  const handlePrintComanda = (order: Order) => {
    const items = (order.items || []).map(i => ({
      qty: Number(i.quantity) || 1,
      name: i.name || (typeof i.product === 'object' ? i.product?.name : '') || 'Producto',
      notes: i.notes || undefined,
      extras: [
        ...((i.flavors || []).map((f: any) =>
          `${(f.quantity ?? f.qty ?? 1) > 1 ? ((f.quantity ?? f.qty) + 'x ') : ''}${f.flavor?.name ?? f.name ?? ''}`.trim()).filter(Boolean)),
        ...(Array.isArray(i.additions) ? i.additions : (i.additions && typeof i.additions === 'object' ? Object.keys(i.additions).map(k => `${k}`) : [])),
        ...(Array.isArray(i.exceptions) ? i.exceptions.map((e: any) => {
          const val = String(e);
          return val.toLowerCase().startsWith('sin ') ? val : `Sin ${val}`;
        }) : []),
      ],
    }));
    const custName = getCustomerName(order);
    const custPhone = order.customerPhone || (order as any).customer_phone;
    const custAddress = order.customerAddress || (order as any).customer_address;
    setTicketToPrint({
      orderId: String(order.id),
      title: `PARA LLEVAR: ${custName}`,
      customerName: custName !== 'Cliente sin nombre' ? custName : undefined,
      customerPhone: custPhone || undefined,
      customerAddress: custAddress || undefined,
      orderNumber: order.orderSequence ?? order.id,
      time: order.createdAt || new Date().toISOString(),
      items,
    });
  };

  const handleToggleComanda = async (orderId: number, currentPrinted: boolean) => {
    try {
      await api.patch(`/orders/${orderId}/comanda-status`, {
        comanda_printed: !currentPrinted,
      });
      toast.success(!currentPrinted ? 'Comanda marcada como impresa' : 'Comanda marcada como pendiente');
      refetch();
    } catch (err: any) {
      toast.error('No se pudo actualizar el estado de la comanda');
    }
  };

  const renderOrderCard = (order: Order) => {
    const statusLabel = ORDER_STATUS_LABELS[order.status] ?? order.status;
    const total = getOrderTotal(order);
    const itemsPreview = order.items?.slice(0, 3) ?? [];
    const remainingItems = Math.max((order.items?.length ?? 0) - itemsPreview.length, 0);
    const itemCount = getItemQuantity(order);
    const isComandaPrinted = Boolean(order.comandaPrinted || order.comanda_printed);
    const printedTime = formatPrintTime(order.comandaPrintedAt || order.comanda_printed_at);

    return (
      <div role="listitem" className="h-full" key={order.id}>
        <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-border bg-surface p-4 shadow-card transition-shadow hover:shadow-card/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/70 focus-within:ring-offset-2 focus-within:ring-offset-surface">
          <div className="space-y-3">
            <header className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-[clamp(18px,2.4vw,22px)] font-semibold text-text">
                  {getCustomerName(order)}
                </p>
                {(order.customerAddress || (order as any).customer_address) && (
                  <p className="truncate text-xs font-medium text-text mt-0.5" title={order.customerAddress || (order as any).customer_address}>
                    📍 {order.customerAddress || (order as any).customer_address}
                  </p>
                )}
                {(order.customerPhone || (order as any).customer_phone) && (
                  <p className="text-xs text-text-muted mt-0.5">
                    📞 {order.customerPhone || (order as any).customer_phone}
                  </p>
                )}
                <p className="text-xs text-text-muted mt-0.5">
                  {itemCount} artículo{itemCount === 1 ? '' : 's'}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <span className="inline-flex min-h-[26px] items-center rounded-full bg-muted px-2.5 text-xs font-semibold uppercase tracking-wide text-text">
                  {statusLabel}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border ${
                    isComandaPrinted
                      ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-300/80 dark:border-emerald-700/60'
                      : 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-300/80 dark:border-amber-700/60'
                  }`}
                  title={isComandaPrinted ? (printedTime ? `Comanda impresa a las ${printedTime}` : 'Comanda impresa') : 'Comanda pendiente por imprimir'}
                >
                  {isComandaPrinted ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                      <span>Impresa{printedTime ? ` ${printedTime}` : ''}</span>
                    </>
                  ) : (
                    <>
                      <Printer className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                      <span>Sin imprimir</span>
                    </>
                  )}
                </span>
              </div>
            </header>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-text">
                Total: {formatCurrency(total)}
              </p>
              <ul className="space-y-1 text-xs text-text-muted">
                {itemsPreview.map((item) => (
                  <li key={item.id}>{getItemDisplayName(item)}</li>
                ))}
                {remainingItems > 0 ? (
                  <li>+ {remainingItems} ítem{remainingItems === 1 ? '' : 's'} más</li>
                ) : null}
              </ul>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDetailOrder(order)}
            >
              Ver
            </Button>
            {order.paid ? (
              <Button
                type="button"
                className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white"
                onClick={() => {
                  updateStatus.mutate(
                    { id: order.id, status: 'DELIVERED' },
                    {
                      onSuccess: () => {
                        toast.success('Pedido entregado');
                        refetch();
                      },
                    }
                  );
                }}
                disabled={updateStatus.isPending}
              >
                Entregar
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setChargingOrder(order)}
              >
                Cobrar
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className={`gap-1.5 ${
                !isComandaPrinted
                  ? 'border-amber-500/70 bg-amber-50/80 text-amber-900 hover:bg-amber-100 hover:border-amber-600 dark:bg-amber-950/40 dark:border-amber-600 dark:text-amber-200 shadow-sm'
                  : 'text-text hover:bg-muted/40'
              }`}
              onClick={() => handlePrintComanda(order)}
              title={isComandaPrinted ? "Reimprimir comanda" : "Imprimir comanda"}
            >
              <Printer className="h-4 w-4" />
              <span>{isComandaPrinted ? 'Reimprimir' : 'Comanda'}</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="gap-1.5 text-danger hover:bg-danger/10 border-danger/30"
              onClick={() => setCancellingOrder(order)}
              title="Cancelar pedido"
            >
              <Trash2 className="h-4 w-4" />
              <span>Cancelar</span>
            </Button>
          </div>
        </article>
      </div>
    );
  };

  const stateCard = () => {
    if (isLoading) {
      return (
        <div role="listitem" className="h-full" key="takeaway-loading">
          <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-border bg-surface p-4 shadow-card">
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
              </div>
              <div className="space-y-2">
                <div className="h-3 w-full animate-pulse rounded bg-muted" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
              </div>
            </div>
            <div className="h-10 w-full animate-pulse rounded-full bg-muted" />
          </article>
        </div>
      );
    }

    if (isError) {
      return (
        <div role="listitem" className="h-full" key="takeaway-error">
          <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-amber-300 bg-amber-50/80 p-4 text-amber-900 shadow-card">
            <div className="space-y-2">
              <p className="text-sm font-semibold">No se pudieron cargar los pedidos.</p>
              {errorMessage ? <p className="text-xs">{errorMessage}</p> : null}
            </div>
            <Button type="button" variant="outline" className="w-full" onClick={() => refetch()}>
              Reintentar
            </Button>
          </article>
        </div>
      );
    }

    if (ordersList.length === 0) {
      return (
        <div role="listitem" className="h-full" key="takeaway-empty">
          <article className="flex h-full flex-col justify-center gap-3 rounded-2xl border border-border bg-surface p-4 text-center shadow-card">
            <p className="text-sm font-semibold text-text">Sin pedidos para llevar</p>
            <p className="text-xs text-text-muted">
              Los pedidos creados aparecerán aquí automáticamente.
            </p>
          </article>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div role="listitem" className="h-full" key="takeaway-create">
        <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-border bg-surface p-4 shadow-card transition-shadow hover:shadow-card/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/70 focus-within:ring-offset-2 focus-within:ring-offset-surface">
          <div className="space-y-3">
            <header className="space-y-1">
              <p className="text-[clamp(18px,2.4vw,22px)] font-semibold text-text">
                Nuevo pedido para llevar
              </p>
              <p className="text-sm text-text-muted">
                Crea un pedido sin mesa y envíalo directo a cocina.
              </p>
            </header>
          </div>
          <Button
            type="button"
            className="min-h-[44px] w-full rounded-full text-[length:clamp(15px,2.4vw,17px)]"
            onClick={() => navigate('/pos/build/takeaway')}
          >
            Nuevo pedido para llevar
          </Button>
        </article>
      </div>
      {ordersList.map((order: Order) => renderOrderCard(order))}
      {stateCard()}
      <TakeawayOrderDetail
        open={detailOrder !== null}
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
        onCancel={() => {
          if (detailOrder) {
            setCancellingOrder(detailOrder);
            setDetailOrder(null);
          }
        }}
        onToggleComanda={handleToggleComanda}
        onPrintComanda={(order) => {
          setDetailOrder(null);
          handlePrintComanda(order);
        }}
      />
      <OrderTicketModal
        data={ticketToPrint}
        onClose={() => setTicketToPrint(null)}
        onSuccess={() => refetch()}
      />
      {chargingOrder ? (
        <TakeawayChargeModal
          orderId={chargingOrder.id}
          open={!!chargingOrder}
          onClose={() => setChargingOrder(null)}
          onSuccess={() => {
            if (detailOrder?.id === chargingOrder.id) {
              setDetailOrder(null);
            }
            setChargingOrder(null);
            refetch();
          }}
          initialAmount={getOrderTotal(chargingOrder)}
        />
      ) : null}
      {cancellingOrder ? (
        <ConfirmCancelOrderModal
          open={!!cancellingOrder}
          order={cancellingOrder}
          loading={cancelOrder.isPending}
          onClose={() => setCancellingOrder(null)}
          onConfirm={(reason) => {
            cancelOrder.mutate(
              {
                orderId: cancellingOrder.id,
                tableId: typeof cancellingOrder.table === 'object' && cancellingOrder.table !== null ? (cancellingOrder.table as any).number : undefined,
              },
              {
                onSuccess: () => {
                  setCancellingOrder(null);
                  refetch();
                },
              }
            );
          }}
        />
      ) : null}
    </>
  );
}

export default TakeawayBoard;
